import React, { forwardRef } from 'react';
import { STORE_BRANDING, getStoreLogoUrl } from '../../../config/storeBranding';

/** Largura Instagram. Exportamos em 1080px. */
export const BANNER_WIDTH = 1080;

export type BannerVehicleItem = {
  id: number;
  title: string;
  brand: string;
  model: string;
  year: number;
  imageUrl: string | null;
  priceFrom: number | null;
  priceTo: number;
  badge?: string | null;
};

export type InstagramPromoBannerProps = {
  titleMain: string;
  titleSub: string;
  dateLabel: string;
  subtitle: string;
  vehicles: BannerVehicleItem[];
  legalNote?: string;
};

const fontDisplay = '"Bebas Neue", "Arial Black", Impact, sans-serif';
const fontHeavy = '"Archivo Black", "Arial Black", sans-serif';
const fontUi = '"Outfit", "DM Sans", Arial, Helvetica, sans-serif';
const fontBody = '"DM Sans", Arial, Helvetica, sans-serif';

const splitMoney = (value: number) => {
  const formatted = value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [intPart, cents] = formatted.split(',');
  return { intPart: intPart || '0', cents: cents || '00' };
};

/**
 * Separa nome comercial (COROLLA ALTIS) das especificações (1.8 16V AUT. HÍBRIDO).
 */
const splitModelNameAndSpecs = (model: string): { name: string; specs: string } => {
  const raw = (model || '').trim();
  if (!raw) return { name: '', specs: '' };

  const tokens = raw.split(/\s+/);
  const specStart = tokens.findIndex((t) =>
    /^(\d+([.,]\d+)?[lLvV]?|\d+(\.\d+)?|AUT\.?|ATM|MT|CVT|FLEX|H[IÍ]BRIDO|HYBRID|TURBO|DIESEL|GASOLINA|4X4|4X2|16V|8V|20V|\(.*\))$/i.test(
      t.replace(/,$/, '')
    )
  );

  if (specStart <= 0) {
    return { name: raw.toUpperCase(), specs: '' };
  }

  return {
    name: tokens.slice(0, specStart).join(' ').toUpperCase(),
    specs: tokens.slice(specStart).join(' ').toUpperCase(),
  };
};

const DateHeadline: React.FC<{ text: string; red: string }> = ({ text, red }) => {
  const upper = (text || '').toUpperCase();
  // Destaca a parte da data (números / mês) em vermelho
  const match = upper.match(/^(.*?\b(?:DIAS?|DIA)\s+)(.+)$/i);
  const baseStyle: React.CSSProperties = {
    fontFamily: fontHeavy,
    fontSize: 38,
    letterSpacing: 0.4,
    color: '#111',
  };
  if (!match) {
    // Ex.: "16 DE SETEMBRO A 18 DE OUTUBRO" — pinta tudo após o primeiro número
    const alt = upper.match(/^([^\d]*)(\d.+)$/);
    if (alt) {
      return (
        <span style={baseStyle}>
          {alt[1]}
          <span style={{ color: red }}>{alt[2]}</span>
        </span>
      );
    }
    return <span style={baseStyle}>{upper}</span>;
  }
  return (
    <span style={baseStyle}>
      {match[1]}
      <span style={{ color: red }}>{match[2]}</span>
    </span>
  );
};

const IconWhatsAppMini = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="12" fill="#25D366" />
    <path
      fill="#fff"
      d="M17.5 14.3c-.3-.1-1.6-.8-1.8-.9-.2-.1-.4-.1-.6.1-.2.3-.7.9-.8 1-.2.1-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.8-.8-1.4-1.7-1.6-2-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.6-1.5-.8-2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-.9.9-.9 2.1 0 1.2.9 2.4 1 2.5.1.2 1.8 2.8 4.4 3.9 2.6 1.1 2.6.7 3.1.7.5 0 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2z"
    />
  </svg>
);

const IconInstagramMini = ({ red }: { red: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect width="24" height="24" rx="6" fill={red} />
    <rect x="6" y="6" width="12" height="12" rx="4" stroke="#fff" strokeWidth="1.8" fill="none" />
    <circle cx="12" cy="12" r="3.2" stroke="#fff" strokeWidth="1.8" fill="none" />
    <circle cx="16.2" cy="7.8" r="1.1" fill="#fff" />
  </svg>
);

const PriceBlock: React.FC<{ from: number | null; to: number; red: string; large: boolean }> = ({
  from,
  to,
  red,
  large,
}) => {
  const { intPart, cents } = splitMoney(to);
  const mainSize = large ? 72 : 58;
  const rsSize = large ? 28 : 22;

  return (
    <div>
      {from != null && from > to ? (
        <div
          style={{
            fontFamily: fontBody,
            fontSize: 16,
            fontWeight: 600,
            color: '#777',
            marginBottom: 2,
          }}
        >
          De:{' '}
          <span style={{ textDecoration: 'line-through' }}>
            R${' '}
            {from.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span
          style={{
            fontFamily: fontHeavy,
            fontSize: rsSize,
            color: '#111',
            marginTop: large ? 18 : 14,
            letterSpacing: 0.5,
          }}
        >
          Por: <span style={{ color: red }}>R$</span>
        </span>
        <div style={{ display: 'flex', alignItems: 'flex-start', lineHeight: 1 }}>
          <span
            style={{
              fontFamily: fontDisplay,
              fontSize: mainSize,
              color: '#111',
              letterSpacing: 0.5,
            }}
          >
            {intPart}
          </span>
          <span
            style={{
              fontFamily: fontDisplay,
              fontSize: mainSize * 0.45,
              color: '#111',
              marginTop: mainSize * 0.12,
              letterSpacing: 0.5,
            }}
          >
            ,{cents}
          </span>
        </div>
      </div>
    </div>
  );
};

const InstagramPromoBanner = forwardRef<HTMLDivElement, InstagramPromoBannerProps>(
  function InstagramPromoBanner(
    {
      titleMain,
      titleSub,
      dateLabel,
      subtitle,
      vehicles,
      legalNote = 'Imagens ilustrativas. Ofertas válidas enquanto durarem os estoques. Consulte condições na loja.',
    },
    ref
  ) {
    const red = STORE_BRANDING.brandRed;
    const logoUrl = getStoreLogoUrl();
    const count = Math.max(vehicles.length, 1);
    const rowH = count <= 2 ? 400 : count === 3 ? 320 : 265;
    const priceLarge = count <= 3;

    return (
      <div
        ref={ref}
        style={{
          width: BANNER_WIDTH,
          background: '#ffffff',
          color: '#111',
          fontFamily: fontUi,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ===== HEADER (branco limpo, como o exemplo) ===== */}
        <div
          style={{
            position: 'relative',
            padding: '36px 44px 8px',
            background: '#fff',
            overflow: 'hidden',
          }}
        >
          {/* linhas de velocidade atrás do 48H */}
          <div
            style={{
              position: 'absolute',
              left: 24,
              top: 52,
              width: 160,
              height: 90,
              opacity: 0.55,
              background: `repeating-linear-gradient(
                0deg,
                transparent 0 11px,
                ${red} 11px 15px
              )`,
              transform: 'skewY(-8deg)',
              clipPath: 'polygon(0 15%, 100% 0, 100% 100%, 0 85%)',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 20,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: fontDisplay,
                  fontSize: 132,
                  lineHeight: 0.78,
                  color: red,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontStyle: 'italic',
                }}
              >
                {titleMain || '48H'}
              </div>

              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                <div
                  style={{
                    background: '#111',
                    color: '#fff',
                    fontFamily: fontHeavy,
                    fontSize: 26,
                    fontStyle: 'italic',
                    letterSpacing: 1.5,
                    padding: '7px 22px',
                    textTransform: 'uppercase',
                    transform: 'skewX(-12deg)',
                    boxShadow: '0 4px 0 rgba(0,0,0,0.12)',
                  }}
                >
                  <span style={{ display: 'inline-block', transform: 'skewX(12deg)' }}>
                    {titleSub || 'DE VENDAS'}
                  </span>
                </div>
                <div
                  style={{
                    background: red,
                    color: '#fff',
                    fontFamily: fontHeavy,
                    fontSize: 24,
                    letterSpacing: 1,
                    padding: '8px 24px',
                    textTransform: 'uppercase',
                    transform: 'skewX(-12deg)',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
                  }}
                >
                  <span style={{ display: 'inline-block', transform: 'skewX(12deg)' }}>
                    {STORE_BRANDING.name}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 8,
                paddingTop: 8,
              }}
            >
              <img
                src={logoUrl}
                alt={STORE_BRANDING.name}
                style={{
                  height: 88,
                  width: 'auto',
                  maxWidth: 260,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: 28, position: 'relative' }}>
            <DateHeadline text={dateLabel} red={red} />
            <div
              style={{
                marginTop: 8,
                fontFamily: fontBody,
                fontSize: 18,
                fontWeight: 500,
                color: '#222',
                maxWidth: 720,
                lineHeight: 1.35,
              }}
            >
              {subtitle || STORE_BRANDING.tagline}
            </div>
          </div>
        </div>

        {/* ===== VEÍCULOS — sem linhas divisórias ===== */}
        <div style={{ flex: 1, padding: '12px 36px 8px', background: '#fff' }}>
          {vehicles.map((v) => {
            const yearShort = String(v.year || '').slice(-2);
            const brandLabel = (v.brand || '').trim().toUpperCase();
            const { name: modelName, specs: modelSpecs } = splitModelNameAndSpecs(v.model || '');
            const specsWithYear = [modelSpecs, yearShort].filter(Boolean).join(' ');

            return (
              <div
                key={v.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: rowH,
                  padding: '6px 0 10px',
                  gap: 8,
                }}
              >
                {/* Texto esquerda */}
                <div
                  style={{
                    flex: '0 1 48%',
                    minWidth: 0,
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 10,
                  }}
                >
                  {v.badge ? (
                    <div
                      style={{
                        alignSelf: 'flex-start',
                        fontFamily: fontUi,
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing: 1.2,
                        textTransform: 'uppercase',
                        color: '#111',
                      }}
                    >
                      {v.badge}
                    </div>
                  ) : null}

                  {/* Faixa vermelha: nome pesado + specs leves */}
                  <div
                    style={{
                      alignSelf: 'flex-start',
                      maxWidth: '100%',
                      background: red,
                      color: '#fff',
                      padding: '11px 20px 11px 16px',
                      borderRadius: '4px 18px 18px 4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      lineHeight: 1.15,
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        fontFamily: fontUi,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 1.8,
                        opacity: 0.9,
                        marginBottom: 3,
                        textTransform: 'uppercase',
                      }}
                    >
                      {brandLabel}
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        gap: '0 10px',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: fontHeavy,
                          fontSize: count > 3 ? 22 : 26,
                          letterSpacing: 0.6,
                          textTransform: 'uppercase',
                        }}
                      >
                        {modelName || (v.model || v.title).toUpperCase()}
                      </span>
                      {specsWithYear ? (
                        <span
                          style={{
                            fontFamily: fontUi,
                            fontSize: count > 3 ? 15 : 17,
                            fontWeight: 500,
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                            opacity: 0.92,
                          }}
                        >
                          {specsWithYear}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <PriceBlock
                    from={v.priceFrom}
                    to={v.priceTo}
                    red={red}
                    large={priceLarge}
                  />
                </div>

                {/* Foto + palco 3D */}
                <div
                  style={{
                    flex: '1 1 52%',
                    position: 'relative',
                    height: rowH - 8,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    perspective: 900,
                    overflow: 'visible',
                  }}
                >
                  {/* brilho ambiental atrás do carro */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '6%',
                      bottom: '28%',
                      width: '70%',
                      height: '55%',
                      borderRadius: '50%',
                      background: `radial-gradient(ellipse at center, ${red}55 0%, ${red}18 35%, transparent 70%)`,
                      filter: 'blur(2px)',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* halo de luz no chão */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '10%',
                      bottom: 18,
                      width: '72%',
                      height: 36,
                      borderRadius: '50%',
                      background: `radial-gradient(ellipse at center, ${red}66 0%, transparent 70%)`,
                      filter: 'blur(6px)',
                    }}
                  />

                  {/* sombra projetada no chão */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '14%',
                      bottom: 20,
                      width: '68%',
                      height: 22,
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.28)',
                      filter: 'blur(10px)',
                      transform: 'scaleX(1.05)',
                    }}
                  />

                  {/* pedestal 3D — face superior */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '4%',
                      bottom: 42,
                      width: '86%',
                      height: 34,
                      background: `linear-gradient(180deg, ${red} 0%, #b91c1c 100%)`,
                      clipPath: 'polygon(10% 0, 100% 0, 90% 100%, 0 100%)',
                      boxShadow: `0 0 0 1px rgba(255,255,255,0.15) inset, 0 14px 28px rgba(0,0,0,0.28)`,
                      transform: 'rotateX(48deg) translateZ(0)',
                      transformOrigin: 'center bottom',
                    }}
                  />
                  {/* pedestal 3D — face frontal (espessura) */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '4%',
                      bottom: 28,
                      width: '86%',
                      height: 16,
                      background: `linear-gradient(180deg, #8f1516 0%, #5c0d0e 100%)`,
                      clipPath: 'polygon(0 0, 90% 0, 90% 100%, 0 100%)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.25)',
                    }}
                  />
                  {/* brilho no topo do pedestal */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '12%',
                      bottom: 58,
                      width: '62%',
                      height: 6,
                      borderRadius: 8,
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                      opacity: 0.7,
                      pointerEvents: 'none',
                    }}
                  />

                  {v.imageUrl ? (
                    <div
                      style={{
                        position: 'relative',
                        zIndex: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        height: '100%',
                        transform: 'translateZ(40px) rotateY(-6deg)',
                        transformStyle: 'preserve-3d',
                      }}
                    >
                      <img
                        src={v.imageUrl}
                        alt={v.title}
                        style={{
                          maxHeight: rowH - 56,
                          maxWidth: '96%',
                          objectFit: 'contain',
                          marginBottom: 48,
                          filter:
                            'drop-shadow(0 18px 24px rgba(0,0,0,0.4)) drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                        }}
                      />
                      {/* reflexo 3D no chão */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 12,
                          left: 0,
                          right: 0,
                          height: Math.round(rowH * 0.28),
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          overflow: 'hidden',
                          pointerEvents: 'none',
                          opacity: 0.35,
                          maskImage:
                            'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 85%)',
                          WebkitMaskImage:
                            'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 85%)',
                        }}
                      >
                        <img
                          src={v.imageUrl}
                          alt=""
                          aria-hidden
                          style={{
                            maxHeight: rowH - 56,
                            maxWidth: '96%',
                            objectFit: 'contain',
                            transform: 'scaleY(-1)',
                            filter: 'blur(0.5px) brightness(0.85)',
                            marginTop: -((rowH - 56) * 0.72),
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        position: 'relative',
                        zIndex: 3,
                        marginBottom: 56,
                        padding: '24px 32px',
                        background: '#f3f3f3',
                        borderRadius: 8,
                        color: '#999',
                        fontFamily: fontUi,
                        fontWeight: 700,
                        fontSize: 15,
                        boxShadow: '0 12px 24px rgba(0,0,0,0.12)',
                      }}
                    >
                      Sem foto
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ===== FOOTER ===== */}
        <div
          style={{
            background: '#0a0a0a',
            color: '#fff',
            padding: '22px 36px 16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
            }}
          >
            <img
              src={logoUrl}
              alt={STORE_BRANDING.name}
              style={{
                height: 44,
                width: 'auto',
                maxWidth: 200,
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
                display: 'block',
              }}
            />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 28,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <IconWhatsAppMini />
                <div style={{ fontFamily: fontUi, fontSize: 16, fontWeight: 700 }}>
                  WhatsApp: {STORE_BRANDING.whatsappDisplay}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IconInstagramMini red={red} />
                <div style={{ fontFamily: fontUi, fontSize: 15, fontWeight: 600 }}>
                  @{STORE_BRANDING.instagram}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              fontFamily: fontBody,
              fontSize: 10,
              lineHeight: 1.4,
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            {legalNote}
          </div>
        </div>
      </div>
    );
  }
);

export default InstagramPromoBanner;
